import { useEffect, useRef, useState, type RefObject } from 'react';
import '../../styles.css';
import { Brand } from './Brand';
import { AuthCallback } from './auth/AuthCallback';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { useAuth } from './auth/AuthProvider';
import { Dashboard } from './dashboard/Dashboard';
import { CONTACT_EMAIL, LegalPage, legalHashPage } from './LegalPages';
import { api, isApiConfigured, type Job, type Package, type Usage } from './lib/api';
import { initializePaddle } from './lib/paddle';
import { usePath } from './lib/routing';

function App() {
  const [toast, setToast] = useState('');
  const [authMode, setAuthMode] = useState<'signup' | 'login'>('signup');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [usage, setUsage] = useState<Usage | null>(null);
  const [role, setRole] = useState('user');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [pageHash, setPageHash] = useState(() => window.location.hash);
  const [navOpen, setNavOpen] = useState(false);
  const authDialog = useRef<HTMLDialogElement>(null);
  const { user, session, signIn, signUp, signInWithGoogle, signOut } = useAuth();
  const { page, navigate } = usePath();

  useEffect(() => {
    const onHashChange = () => setPageHash(window.location.hash);
    const onResize = () => { if (window.innerWidth > 900) setNavOpen(false); };
    window.addEventListener('hashchange', onHashChange);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = navOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [navOpen]);

  const notify = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2600);
  };

  useEffect(() => {
    if (!isApiConfigured()) return;
    let cancelled = false;
    void api.packages().then((response) => {
      if (!cancelled) setPackages(response.items);
    }).catch(() => undefined);
    if (session) {
      void Promise.all([api.me(session), api.jobs(session)]).then(([me, history]) => {
        if (!cancelled) {
          setUsage(me.usage);
          setRole(me.user.role);
          setJobs(history.items);
          void initializePaddle(me.user.paddle_customer_id);
        }
      }).catch(() => undefined);
    } else {
      setUsage(null);
      setRole('user');
      setJobs([]);
      void initializePaddle(null);
    }
    return () => { cancelled = true; };
  }, [session]);

  const openAuth = (mode: 'signup' | 'login') => {
    setAuthMode(mode);
    authDialog.current?.showModal();
  };

  const enterApp = () => {
    authDialog.current?.close();
    navigate('/app');
  };

  const handleCheckout = async () => {
    if (!user || !session) { openAuth('signup'); return; }
    const studentPackage = packages.find((item) => item.allowance === 3) ?? packages[0];
    if (!studentPackage || !isApiConfigured()) { notify('Checkout is not configured yet.'); return; }
    try {
      const checkout = await api.checkout(session, studentPackage.id);
      window.location.assign(checkout.checkout_url);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Checkout is temporarily unavailable.');
    }
  };

  const legalPage = legalHashPage(pageHash);
  if (legalPage) return <LegalPage page={legalPage} />;
  if (page === 'callback') return <AuthCallback />;

  const inApp = page === 'humanizer' || page === 'profile' || page === 'settings' || page === 'history' || page === 'admin';
  if (inApp) {
    return (
      <>
        <ProtectedRoute fallback={<RouteSignIn onLogin={() => openAuth('login')} />}>
          {user && session ? (
            <Dashboard
              page={page}
              user={user}
              session={session}
              role={role}
              usage={usage}
              jobs={jobs}
              packages={packages}
              onNavigate={navigate}
              onSignOut={() => { void signOut(); navigate('/'); }}
              onJobsChange={setJobs}
              onUsageChange={setUsage}
              notify={notify}
            />
          ) : null}
        </ProtectedRoute>
        {toast && <div className="toast visible" role="status" aria-live="polite"><span>✓</span><p>{toast}</p></div>}
        <AuthDialog
          authDialog={authDialog}
          authMode={authMode}
          setAuthMode={setAuthMode}
          authEmail={authEmail}
          setAuthEmail={setAuthEmail}
          authPassword={authPassword}
          setAuthPassword={setAuthPassword}
          signIn={signIn}
          signUp={signUp}
          signInWithGoogle={signInWithGoogle}
          notify={notify}
          onAuthed={enterApp}
        />
      </>
    );
  }

  return (
    <>
      <div className="landing">
      <div className="page-glow glow-one" />
      <div className="page-glow glow-two" />
      <header className={`site-header ${navOpen ? 'menu-open' : ''}`} id="top">
        <nav className="nav shell" aria-label="Main navigation">
          <a href="#top" aria-label="Phrasio home" onClick={() => setNavOpen(false)}><Brand /></a>
          <div id="site-nav-links" className={`nav-links ${navOpen ? 'open' : ''}`}>
            <a href="#features" onClick={() => setNavOpen(false)}>How it works</a>
            <a href="#pricing" onClick={() => setNavOpen(false)}>Pricing</a>
            <a href="#faq" onClick={() => setNavOpen(false)}>FAQ</a>
            <div className="nav-mobile-actions">
              {user ? (
                <>
                  <a className="button button-dark button-small" href="/app" onClick={(event) => { event.preventDefault(); setNavOpen(false); navigate('/app'); }}>Open dashboard</a>
                  <button className="text-btn" type="button" onClick={() => { setNavOpen(false); void signOut(); }}>Log out</button>
                </>
              ) : (
                <>
                  <button className="text-btn" type="button" onClick={() => { setNavOpen(false); openAuth('login'); }}>Log in</button>
                  <button className="button button-dark button-small" type="button" onClick={() => { setNavOpen(false); openAuth('signup'); }}>Try it free <span aria-hidden="true">↗</span></button>
                </>
              )}
            </div>
          </div>
          <div className="nav-actions">
            {user ? (
              <>
                <a className="button button-dark button-small" href="/app" onClick={(event) => { event.preventDefault(); navigate('/app'); }}>Open dashboard</a>
                <button className="text-btn" onClick={() => void signOut()}>Log out</button>
              </>
            ) : (
              <>
                <button className="text-btn" onClick={() => openAuth('login')}>Log in</button>
                <button className="button button-dark button-small" onClick={() => openAuth('signup')}>Try it free <span aria-hidden="true">↗</span></button>
              </>
            )}
          </div>
          <button
            className={`mobile-menu ${navOpen ? 'open' : ''}`}
            type="button"
            aria-expanded={navOpen}
            aria-controls="site-nav-links"
            aria-label={navOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setNavOpen((open) => !open)}
          >
            <span /><span />
          </button>
        </nav>
      </header>

      <main>
        <section className="hero shell" aria-labelledby="hero-title">
          <div className="hero-badge reveal"><span className="badge-dot" /> Built for clearer, more natural writing</div>
          <h1 id="hero-title" className="reveal delay-1">Keep your meaning.<br /><span>Find your voice.</span></h1>
          <p className="hero-copy reveal delay-2">Turn stiff, generic drafts into writing that feels natural and genuinely yours—without losing the facts, citations, or argument underneath.</p>
          <div className="hero-actions reveal delay-3">
            {user ? (
              <a className="button button-primary" href="/app" onClick={(event) => { event.preventDefault(); navigate('/app'); }}>Open your workspace <span aria-hidden="true">→</span></a>
            ) : (
              <button className="button button-primary" onClick={() => openAuth('signup')}>Humanize your first draft <span aria-hidden="true">→</span></button>
            )}
            <a className="sample-link" href="#features"><span className="sample-icon" aria-hidden="true">✦</span><span>See how it works</span><b aria-hidden="true">→</b></a>
          </div>
          <div className="trust-row reveal delay-3" aria-label="Product assurances"><span><b>✓</b> Meaning-first rewrites</span><span><b>✓</b> No credit card</span><span><b>✓</b> Your text stays yours</span></div>
        </section>

        <section className="proof-strip" aria-label="Product highlights"><div className="shell proof-grid"><div><strong>Meaning first</strong><span>Every rewrite protects the point you’re making.</span></div><div><strong>Citation aware</strong><span>Academic references stay exactly where they belong.</span></div><div><strong>Your voice</strong><span>Choose a tone without flattening your personality.</span></div><div><strong>Private by design</strong><span>Your drafts aren’t used to train public models.</span></div></div></section>
        <section className="features shell" id="features" aria-labelledby="features-title"><div className="section-intro"><span className="eyebrow">HOW PHRASIO HELPS</span><h2 id="features-title">A thoughtful editor,<br />not a word spinner.</h2><p>Natural writing is more than swapping synonyms. Phrasio focuses on rhythm, clarity, and the ideas that need to stay intact.</p></div><div className="feature-grid"><article className="feature-card large-card card-ink"><div className="feature-number">01</div><div className="meaning-visual" aria-hidden="true"><div className="meaning-node">Your idea</div><span className="meaning-line" /><div className="meaning-check">✓</div><span className="meaning-line" /><div className="meaning-node mint">Clearer words</div></div><div><span className="card-tag">MEANING GUARD</span><h3>Your point survives the rewrite.</h3><p>Key claims, figures, citations, and specialist terms are checked so polish doesn’t come at the cost of accuracy.</p></div></article><article className="feature-card"><div className="feature-number">02</div><div className="rhythm-visual" aria-hidden="true"><span /><span /><span /><span /><span /><span /></div><div><span className="card-tag">NATURAL RHYTHM</span><h3>Sentence flow that feels lived-in.</h3><p>Mixes sentence lengths and removes repetitive patterns while keeping your logic easy to follow.</p></div></article><article className="feature-card"><div className="feature-number">03</div><div className="tone-visual" aria-hidden="true"><span className="selected">Natural</span><span>Academic</span><span>Simple</span></div><div><span className="card-tag">VOICE CONTROL</span><h3>One idea, the right voice.</h3><p>Move from conversational to academic without losing the personality behind the page.</p></div></article></div></section>
        <section className="steps-section"><div className="shell"><div className="section-intro centered"><span className="eyebrow">FROM DRAFT TO DONE</span><h2>Better writing in three calm steps.</h2></div><div className="steps-grid"><article><span className="step-index">1</span><div className="step-icon">↘</div><h3>Drop in your draft</h3><p>Paste anything from a paragraph to an essay section.</p></article><article><span className="step-index">2</span><div className="step-icon">⌁</div><h3>Set the voice</h3><p>Choose the tone and how gently you want it rewritten.</p></article><article><span className="step-index">3</span><div className="step-icon">✦</div><h3>Review with confidence</h3><p>Compare the result and see what Meaning Guard protected.</p></article></div></div></section>
        <section className="quote-section shell"><div className="quote-card"><div className="quote-mark">“</div><blockquote>It doesn’t make my essays sound fake or over-polished. It just helps me say what I already meant—more clearly.</blockquote><div className="quote-person"><span className="avatar">AM</span><span><strong>Amina M.</strong><small>Engineering student</small></span></div><div className="quote-decoration" aria-hidden="true"><span /><span /><span /></div></div></section>
        <section className="pricing shell" id="pricing" aria-labelledby="pricing-title"><div className="section-intro centered"><span className="eyebrow">SIMPLE PRICING</span><h2 id="pricing-title">Start free. Upgrade when it clicks.</h2><p>No confusing credit packs. No impossible promises. Just clear limits and fair student pricing.</p></div><div className="pricing-grid"><article className="price-card"><div><span className="price-name">Free</span><p>For trying the workflow.</p></div><div className="price"><span>RM</span><strong>0</strong><small>/ forever</small></div><ul><li>3,000 words each month</li><li>Natural & Simple voices</li><li>Standard Meaning Guard</li><li>Copy and download</li></ul><button className="button button-outline full-width" onClick={() => openAuth('signup')}>Start free</button></article><article className="price-card featured-price"><span className="popular-pill">MOST POPULAR</span><div><span className="price-name">Student</span><p>For assignments and reports.</p></div><div className="price"><span>RM</span><strong>12</strong><small>/ 3 uses</small></div><ul><li>Three successful rewrites</li><li>Every writing voice</li><li>Meaning Guard checks</li><li>Rewrite history</li></ul><button className="button button-primary full-width" onClick={() => void handleCheckout()}>Choose Student <span>↗</span></button></article><article className="price-card"><div><span className="price-name">Coming soon</span><p>More ways to write.</p></div><div className="price"><span>RM</span><strong>—</strong></div><ul><li>Team and repeat-purchase plans</li><li>Priority processing</li><li>Custom voice profiles</li></ul><button className="button button-outline full-width" onClick={() => notify('More plans are on the way')}>Keep me posted</button></article></div><p className="price-note">The MVP starts with a simple RM12 pack. Taxes and Paddle payment details are shown at checkout.</p></section>
        <section className="faq shell" id="faq" aria-labelledby="faq-title"><div className="faq-intro"><span className="eyebrow">GOOD QUESTIONS</span><h2 id="faq-title">A few things worth knowing.</h2><p>Still curious? <a href={`mailto:${CONTACT_EMAIL}`}>Talk to us</a> and we’ll help.</p></div><div className="accordion">{[['Does Phrasio guarantee a specific AI detector score?', 'No. AI detectors can be inconsistent, so Phrasio is designed around writing quality, natural voice, and meaning preservation—not guaranteed evasion.'], ['Will it change facts or citations in my writing?', 'Meaning Guard is designed to identify names, figures, citations, and key claims so they can be checked during a rewrite. You should still review every final draft.'], ['Can I use it for university assignments?', 'Use it as an editing aid and follow your institution’s academic-integrity and disclosure rules. Your final work should reflect your own understanding and judgment.'], ['What happens to the text I paste?', 'The production version will explain exactly how drafts are handled, how long they are retained, and what controls you have.'], ['Is this version fully functional?', 'Sign in to use the dashboard humanizer, rewrite history, and Paddle checkout when the public environment values are configured.']].map(([question, answer], index) => <FaqItem key={question} question={question} answer={answer} open={index === 0} />)}</div></section>
        <section className="final-cta shell"><div className="cta-card"><div className="cta-orb orb-left" aria-hidden="true" /><div className="cta-orb orb-right" aria-hidden="true" /><span className="eyebrow light">YOUR WORDS, ONLY BETTER</span><h2>Make your next draft<br />sound more like you.</h2><p>Start free and open the writing studio from your dashboard.</p>{user ? <a className="button button-primary" href="/app" onClick={(event) => { event.preventDefault(); navigate('/app'); }}>Go to dashboard <span>→</span></a> : <button className="button button-primary" onClick={() => openAuth('signup')}>Try Phrasio free <span>→</span></button>}</div></section>
      </main>

      <footer className="site-footer"><div className="shell footer-grid"><div className="footer-brand"><a href="#top"><Brand /></a><p>Writing that sounds natural.<br />Meaning that stays yours.</p><div className="status"><span /> All systems calm</div></div><div className="footer-column"><strong>Product</strong>{user ? <a href="/app" onClick={(event) => { event.preventDefault(); navigate('/app'); }}>Humanizer</a> : <button className="footer-link" type="button" onClick={() => openAuth('signup')}>Humanizer</button>}<a href="#features">How it works</a><a href="#pricing">Pricing</a></div><div className="footer-column"><strong>Company</strong><a href="#faq">About</a><a href={`mailto:${CONTACT_EMAIL}`}>Contact</a><a href="#faq">Responsible use</a></div><div className="footer-column"><strong>Legal</strong><a href="#privacy">Privacy</a><a href="#terms">Terms</a><a href="#refunds">Refunds</a></div></div><div className="shell footer-bottom"><span>© 2026 Phrasio. Made for thoughtful writers.</span><span>Malaysia · English (MY)</span></div></footer>
      </div>

      <AuthDialog
        authDialog={authDialog}
        authMode={authMode}
        setAuthMode={setAuthMode}
        authEmail={authEmail}
        setAuthEmail={setAuthEmail}
        authPassword={authPassword}
        setAuthPassword={setAuthPassword}
        signIn={signIn}
        signUp={signUp}
        signInWithGoogle={signInWithGoogle}
        notify={notify}
        onAuthed={enterApp}
      />
      {toast && <div className="toast visible" role="status" aria-live="polite"><span>✓</span><p>{toast}</p></div>}
    </>
  );
}

function FaqItem({ question, answer, open }: { question: string; answer: string; open: boolean }) {
  const [expanded, setExpanded] = useState(open);
  return <article className={`faq-item ${expanded ? 'open' : ''}`}><button aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}><span>{question}</span><b>{expanded ? '−' : '+'}</b></button><div className="faq-answer"><p>{answer}</p></div></article>;
}

function RouteSignIn({ onLogin }: { onLogin: () => void }) {
  return (
    <main className="auth-gate">
      <div className="auth-gate-card">
        <h1>Sign in to continue</h1>
        <p>The writing studio lives in your dashboard after you log in.</p>
        <div className="auth-gate-actions">
          <button className="button button-primary" type="button" onClick={onLogin}>Log in</button>
          <a className="button button-outline" href="/">Back to Phrasio</a>
        </div>
      </div>
    </main>
  );
}

function AuthDialog({
  authDialog,
  authMode,
  setAuthMode,
  authEmail,
  setAuthEmail,
  authPassword,
  setAuthPassword,
  signIn,
  signUp,
  signInWithGoogle,
  notify,
  onAuthed,
}: {
  authDialog: RefObject<HTMLDialogElement | null>;
  authMode: 'signup' | 'login';
  setAuthMode: (mode: 'signup' | 'login') => void;
  authEmail: string;
  setAuthEmail: (value: string) => void;
  authPassword: string;
  setAuthPassword: (value: string) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<{ needsEmailConfirmation: boolean }>;
  signInWithGoogle: () => Promise<void>;
  notify: (message: string) => void;
  onAuthed: () => void;
}) {
  return (
    <dialog className="auth-modal" ref={authDialog} aria-labelledby="authTitle">
      <div className="modal-shell">
        <button className="modal-close" aria-label="Close" onClick={() => authDialog.current?.close()}>×</button>
        <div className="modal-brand"><span className="brand-mark" aria-hidden="true"><span /></span></div>
        <h2 id="authTitle">{authMode === 'signup' ? 'Welcome to Phrasio' : 'Good to see you again'}</h2>
        <p>{authMode === 'signup' ? 'Create your free account and find your natural voice.' : 'Log in to continue working on your drafts.'}</p>
        <div className="auth-tabs" role="tablist">
          <button className={authMode === 'signup' ? 'active' : ''} onClick={() => setAuthMode('signup')}>Sign up</button>
          <button className={authMode === 'login' ? 'active' : ''} onClick={() => setAuthMode('login')}>Log in</button>
        </div>
        <button className="social-login" type="button" onClick={() => { void signInWithGoogle().catch((error: unknown) => notify(error instanceof Error ? error.message : 'Google sign-in failed')); }}><span className="google-g">G</span> Continue with Google</button>
        <div className="or-divider"><span>or continue with email</span></div>
        <form onSubmit={async (event) => {
          event.preventDefault();
          try {
            if (authMode === 'signup') {
              const response = await signUp(authEmail, authPassword);
              if (response.needsEmailConfirmation) {
                authDialog.current?.close();
                notify('Check your email to verify your account');
              } else {
                notify('Account created');
                onAuthed();
              }
            } else {
              await signIn(authEmail, authPassword);
              notify('Welcome back');
              onAuthed();
            }
          } catch (error) {
            notify(error instanceof Error ? error.message : 'Authentication failed');
          }
        }}>
          <label><span>Email address</span><input type="email" placeholder="you@university.edu" value={authEmail} onChange={(event) => setAuthEmail(event.target.value)} required /></label>
          <label><span>Password</span><input type="password" placeholder="At least 8 characters" minLength={8} value={authPassword} onChange={(event) => setAuthPassword(event.target.value)} required /></label>
          <button className="button button-primary full-width" type="submit">{authMode === 'signup' ? 'Create free account' : 'Log in'}</button>
        </form>
        <p className="modal-terms">By continuing, you agree to our <a href="#terms">Terms</a> and <a href="#privacy">Privacy Policy</a>.</p>
      </div>
    </dialog>
  );
}

export default App;
